import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import type { StateCreator } from 'zustand/vanilla';

import { createSelectors } from '@/libs/zustand';
import { StorageStoreDefault } from '@/models/store';
import { isArrayEqual } from '@/utils/objects';
import { EncryptedStorage } from '@/utils/storage';

import { AuthData, AuthUser } from '../types/auth';

const PERSIST_KEY = 'abonoComAuth';

export interface AuthState {
    authUser: AuthUser | null;
    authData: AuthData | null;
    isAuthenticated: boolean;
}

interface AuthActions {
    setAuthUser: (authUser: AuthUser) => void;
    setAuthData: (authData: AuthData) => void;
    setIsAuthenticated: (isAuthenticated: boolean) => void;
    resetAuthState: () => void;
}

type StateAndActions = AuthState & AuthActions & StorageStoreDefault;

const store: StateCreator<StateAndActions> = (set) => ({
    authUser: null,
    authData: null,
    isAuthenticated: false,
    isStorageLoaded: false,

    setAuthUser: (authUser: AuthUser) =>
        set((state) =>
            !state.authUser ||
            state.authUser.id !== authUser.id ||
            state.authUser.fullName !== authUser.fullName ||
            state.authUser.initialsName !== authUser.initialsName ||
            !isArrayEqual(state.authUser.usersMarkets, authUser.usersMarkets)
                ? { authUser, isAuthenticated: true }
                : state,
        ),
    setAuthData: ({ expiredTime, accessToken, refreshToken, remember }: AuthData) =>
        set((state) =>
            state.authData?.expiredTime !== expiredTime ||
            state.authData.accessToken !== accessToken ||
            state.authData.refreshToken !== refreshToken ||
            state.authData.remember !== remember
                ? { authData: { expiredTime, accessToken, refreshToken, remember } }
                : state,
        ),
    setIsAuthenticated: (isAuthenticated: boolean) =>
        set((state) => (state.isAuthenticated !== isAuthenticated ? { isAuthenticated } : state)),
    resetAuthState: () =>
        set((state) =>
            state.authUser !== null || state.authData !== null || state.isAuthenticated
                ? { authUser: null, authData: null, isAuthenticated: false }
                : state,
        ),
    setIsStorageLoaded: () => set((state) => (!state.isStorageLoaded ? { isStorageLoaded: true } : state)),
});

const useAuthStoreBase = create<StateAndActions>()(
    persist(immer<StateAndActions>(store), {
        name: PERSIST_KEY,
        partialize: ({ authUser, authData, isAuthenticated }) => ({ authUser, authData, isAuthenticated }),
        storage: new EncryptedStorage(),
        onRehydrateStorage: () => (state) => {
            state?.setIsStorageLoaded();
        },
    }),
);

export const useAuthStore = createSelectors(useAuthStoreBase);
